import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Truck, Shield, LogOut, Lock, AlertCircle, X } from "lucide-react";

function initials(v) {
  return `${(v.first_name || "?")[0] || ""}${(v.last_name || "?")[0] || ""}`.toUpperCase();
}

export default function SelectProfile() {
  const { user, selectVendor, setupVendorPin, switchCompany, enterAdminSpace, setupAdminPin, logout } = useAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [switching, setSwitching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [adminMode, setAdminMode] = useState(null); // null | "verify" | "setup"
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (user && user.role === "client" && !user.vendor) {
      api.get("/client/vendors").then((r) => setVendors(r.data)).catch(() => setVendors([]));
      api.get("/auth/companies").then((r) => setCompanies(r.data)).catch(() => setCompanies([]));
    }
  }, [user]);

  const changeCompany = async (companyId) => {
    if (switching) return;
    setSwitching(true);
    setVendors(null);
    const res = await switchCompany(companyId);
    if (!res.ok) setError(res.error);
    setSwitching(false);
  };

  if (user === undefined) return null;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.vendor) return <Navigate to="/client" replace />;

  const vendorSetup = selected && selected.has_pin === false;

  const submitPin = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) return;
    if (vendorSetup && pin !== pin2) {
      setError("Les deux codes PIN ne correspondent pas");
      return;
    }
    setChecking(true);
    setError(null);
    const res = vendorSetup ? await setupVendorPin(selected.id, pin) : await selectVendor(selected.id, pin);
    setChecking(false);
    if (res.ok) {
      navigate("/client/new");
    } else {
      setError(res.error);
      setPin("");
      setPin2("");
    }
  };

  const submitAdminPin = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) return;
    if (adminMode === "setup" && pin !== pin2) {
      setError("Les deux codes PIN ne correspondent pas");
      return;
    }
    setChecking(true);
    setError(null);
    const res = adminMode === "setup" ? await setupAdminPin(pin) : await enterAdminSpace(pin);
    setChecking(false);
    if (res.ok) {
      navigate("/company/orders");
    } else {
      setError(res.error);
      setPin("");
      setPin2("");
    }
  };

  const openPin = (v) => {
    if (!v.active) return;
    setSelected(v);
    setAdminMode(null);
    setPin("");
    setPin2("");
    setError(null);
  };

  const openAdmin = () => {
    setSelected(null);
    setAdminMode(user.admin_pin_set ? "verify" : "setup");
    setPin("");
    setPin2("");
    setError(null);
  };

  const activeVendors = (vendors || []).filter((v) => v.active);
  const restrictedVendors = (vendors || []).filter((v) => !v.active);

  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-12" style={{ background: "var(--lc-surface)" }} data-testid="select-profile-page">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: "var(--lc-orange)", boxShadow: "0 4px 12px rgba(255, 90, 31, 0.25)" }}>
          <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div className="leading-tight">
          <div className="display font-bold text-base">LC Services</div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--lc-gray)]">Livraison Cuisine</div>
        </div>
      </div>
      <p className="text-xs text-[var(--lc-gray)] mb-8">{user.name}</p>

      <h1 className="display text-2xl sm:text-3xl font-extrabold tracking-tight mb-4">Qui êtes-vous&nbsp;?</h1>

      {companies.length > 1 && (
        <div className="w-full max-w-2xl mb-6" data-testid="company-switcher">
          <div className="lc-small-label mb-2 text-center">Entreprise</div>
          <div className="flex flex-wrap justify-center gap-2">
            {companies.map((c) => (
              <button
                key={c.id}
                onClick={() => !c.active && changeCompany(c.id)}
                data-testid={`company-switch-${c.id}`}
                disabled={switching}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                  c.active
                    ? "bg-[var(--lc-orange)] text-white border-[var(--lc-orange)]"
                    : "bg-white text-[var(--lc-black)] border-[var(--lc-border)] hover:border-[var(--lc-orange)] hover:text-[var(--lc-orange)]"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {vendors === null && <div className="text-sm text-[var(--lc-gray)]">Chargement…</div>}

      {vendors !== null && (
        <div className="w-full max-w-2xl">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="vendor-profiles-grid">
            {activeVendors.map((v) => (
              <button
                key={v.id}
                onClick={() => openPin(v)}
                data-testid={`vendor-profile-${v.id}`}
                className="lc-card p-5 flex flex-col items-center gap-3 hover:border-[var(--lc-orange)] transition-colors group"
              >
                <div className="w-14 h-14 rounded-full bg-[var(--lc-orange-light)] text-[var(--lc-orange)] flex items-center justify-center text-lg font-black group-hover:bg-[var(--lc-orange)] group-hover:text-white transition-colors">
                  {initials(v)}
                </div>
                <div className="text-sm font-bold text-center leading-tight">{v.first_name}<br />{v.last_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--lc-gray)] flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Vendeur
                </div>
              </button>
            ))}

            <button
              onClick={openAdmin}
              data-testid="company-admin-profile"
              className="lc-card p-5 flex flex-col items-center gap-3 hover:border-[var(--lc-black)] transition-colors group border-dashed"
            >
              <div className="w-14 h-14 rounded-full bg-black/5 text-[var(--lc-black)] flex items-center justify-center group-hover:bg-[var(--lc-black)] group-hover:text-white transition-colors">
                <Shield className="w-6 h-6" />
              </div>
              <div className="text-sm font-bold text-center leading-tight">Espace<br />administrateur</div>
              <div className="text-[10px] uppercase tracking-widest text-[var(--lc-gray)] flex items-center gap-1">
                <Lock className="w-3 h-3" /> Gestion &amp; suivi
              </div>
            </button>
          </div>

          {restrictedVendors.length > 0 && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
              {restrictedVendors.map((v) => (
                <div key={v.id} data-testid={`vendor-profile-restricted-${v.id}`} className="lc-card p-5 flex flex-col items-center gap-3 opacity-50 cursor-not-allowed">
                  <div className="w-14 h-14 rounded-full bg-black/5 text-[var(--lc-gray)] flex items-center justify-center text-lg font-black">{initials(v)}</div>
                  <div className="text-sm font-bold text-center leading-tight">{v.first_name}<br />{v.last_name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-red-500">Accès restreint</div>
                </div>
              ))}
            </div>
          )}

          {activeVendors.length === 0 && (
            <p className="mt-6 text-center text-xs text-[var(--lc-gray)]" data-testid="no-vendors-hint">
              Aucun profil vendeur. L'administrateur de votre entreprise peut en créer depuis l'espace administrateur.
            </p>
          )}
        </div>
      )}

      <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="select-profile-logout" className="mt-10 text-xs text-[var(--lc-gray)] hover:text-[var(--lc-black)] inline-flex items-center gap-1.5">
        <LogOut className="w-3.5 h-3.5" /> Se déconnecter
      </button>

      {/* Vendor PIN dialog */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-md w-full max-w-sm p-6 relative" data-testid="pin-dialog">
            <button onClick={() => setSelected(null)} data-testid="pin-dialog-close" className="absolute top-3 right-3 p-2 hover:bg-black/5 rounded">
              <X className="w-4 h-4" />
            </button>
            <div className="flex flex-col items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-[var(--lc-orange-light)] text-[var(--lc-orange)] flex items-center justify-center text-base font-black mb-2">
                {initials(selected)}
              </div>
              <div className="font-bold">{selected.first_name} {selected.last_name}</div>
              <div className="text-xs text-[var(--lc-gray)] text-center mt-1">
                {vendorSetup
                  ? "Première connexion : choisissez votre code PIN à 4 chiffres."
                  : "Saisissez votre code PIN à 4 chiffres"}
              </div>
            </div>

            {error && (
              <div data-testid="pin-error" className="mb-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
              </div>
            )}

            <form onSubmit={submitPin} className="space-y-3">
              <input
                data-testid="pin-input"
                type="password"
                inputMode="numeric"
                maxLength={4}
                autoFocus
                className="lc-input text-center text-3xl tracking-[0.6em] font-bold"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                placeholder="••••"
              />
              {vendorSetup && (
                <div>
                  <div className="text-xs text-[var(--lc-gray)] mb-1 text-center">Confirmez le code PIN</div>
                  <input
                    data-testid="pin-confirm-input"
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    className="lc-input text-center text-3xl tracking-[0.6em] font-bold"
                    value={pin2}
                    onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))}
                    placeholder="••••"
                  />
                </div>
              )}
              <button
                type="submit"
                data-testid="pin-submit-button"
                disabled={pin.length !== 4 || (vendorSetup && pin2.length !== 4) || checking}
                className="lc-btn-primary w-full"
              >
                {checking ? "Vérification…" : vendorSetup ? "Créer mon code et ouvrir ma session" : "Ouvrir ma session"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Admin PIN dialog (verify or first-time setup) */}
      {adminMode && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-md w-full max-w-sm p-6 relative" data-testid="admin-pin-dialog">
            <button onClick={() => setAdminMode(null)} data-testid="admin-pin-dialog-close" className="absolute top-3 right-3 p-2 hover:bg-black/5 rounded">
              <X className="w-4 h-4" />
            </button>
            <div className="flex flex-col items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-black/5 text-[var(--lc-black)] flex items-center justify-center mb-2">
                <Shield className="w-5 h-5" />
              </div>
              <div className="font-bold">Espace administrateur</div>
              <div className="text-xs text-[var(--lc-gray)] text-center mt-1">
                {adminMode === "setup"
                  ? "Première connexion : créez votre code PIN administrateur à 4 chiffres."
                  : "Saisissez votre code PIN administrateur"}
              </div>
            </div>

            {error && (
              <div data-testid="admin-pin-error" className="mb-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
              </div>
            )}

            <form onSubmit={submitAdminPin} className="space-y-3">
              <input
                data-testid="admin-pin-input"
                type="password"
                inputMode="numeric"
                maxLength={4}
                autoFocus
                className="lc-input text-center text-3xl tracking-[0.6em] font-bold"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                placeholder="••••"
              />
              {adminMode === "setup" && (
                <div>
                  <div className="text-xs text-[var(--lc-gray)] mb-1 text-center">Confirmez le code PIN</div>
                  <input
                    data-testid="admin-pin-confirm-input"
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    className="lc-input text-center text-3xl tracking-[0.6em] font-bold"
                    value={pin2}
                    onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))}
                    placeholder="••••"
                  />
                </div>
              )}
              <button
                type="submit"
                data-testid="admin-pin-submit-button"
                disabled={pin.length !== 4 || (adminMode === "setup" && pin2.length !== 4) || checking}
                className="lc-btn-primary w-full"
              >
                {checking ? "Vérification…" : adminMode === "setup" ? "Créer et entrer" : "Entrer"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
